import React, { useCallback, useEffect, useRef, useState } from "react";

// Enter in a single-line field presses the scope's primary button (kb.primaryProps), as a click would;
// Ctrl/Cmd+Enter does it from any field. Same file in inventory-web and finex-web.

const SUBMIT_TYPES = new Set(["", "text", "email", "tel", "url", "password", "number", "checkbox", "radio"]);
// A press of Next or Back moves focus into the new step only if the step changes this soon after.
const STEP_FOCUS_WINDOW_MS = 1500;
// Presses this soon after a step change are the tail of a double-click or held key on Next or Back.
const STEP_SETTLE_MS = 400;

const isVisible = (el: HTMLElement) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden";
const ownScope = (el: Element) => el.closest("[data-form-scope]");
// Phones and tablets without a mouse keep their Return key as it was (it closes the keyboard).
const hasFinePointer = () => typeof window.matchMedia !== "function" || window.matchMedia("(any-pointer: fine)").matches;
// A finger or pen tap counts as touch even on a laptop that also has a mouse or trackpad.
const isTap = (e: React.MouseEvent) => ["touch", "pen"].includes((e.nativeEvent as PointerEvent).pointerType);

// A dialog or sheet rendered inside the scope keeps its Enter to itself.
const inNestedDialog = (el: Element, scope: HTMLElement) => {
  const dialog = el.closest('[role="dialog"], [aria-modal="true"]');
  return !!dialog && dialog !== scope && scope.contains(dialog);
};

// While a modal inside the scope is open, Enter on the page behind it does nothing.
const hasOpenModal = (scope: HTMLElement) =>
  Array.from(scope.querySelectorAll<HTMLElement>('[aria-modal="true"]')).some((el) => el !== scope && isVisible(el));

const belongsTo = (el: HTMLElement, scope: HTMLElement) => ownScope(el) === scope && !inNestedDialog(el, scope) && isVisible(el);

// Dropdowns open their list on focus, so focusing one programmatically pops a list open.
const isCombobox = (el: Element | null | undefined) => el?.getAttribute("role") === "combobox";

const isField = (el: HTMLElement) =>
  ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable || isCombobox(el);

// A control that uses Enter itself (open dropdown, search box) calls preventDefault, which ends here.
const isSubmitField = (el: HTMLElement) =>
  el.tagName === "INPUT" &&
  !el.closest('[data-enter="ignore"], .react-datepicker, .react-datepicker-wrapper') &&
  SUBMIT_TYPES.has((el.getAttribute("type") ?? "").toLowerCase());

// Includes fields of nested sections (a step may hold only an add-row), but not of dialogs.
const firstField = (scope: HTMLElement, skipCombobox = false) =>
  Array.from(scope.querySelectorAll<HTMLElement>('input:not([type="hidden"]), select, textarea')).find(
    (el) =>
      !inNestedDialog(el, scope) && isVisible(el) && !(el as HTMLInputElement).disabled && !(el as HTMLInputElement).readOnly &&
      !(skipCombobox && isCombobox(el)),
  );

// aria-disabled primaries still get the press: some show their errors on click.
const primaryOf = (scope: HTMLElement) =>
  Array.from(scope.querySelectorAll<HTMLElement>("[data-primary-action]")).find(
    (el) => belongsTo(el, scope) && !(el as HTMLButtonElement).disabled,
  );


export interface FormKeyboardOptions {
  // Current step of a multi-step form: a step change pressed through primaryProps or stepProps
  // moves focus into the new step, so it never stays on a button that now reads Save or Cancel.
  step?: unknown;
  autoFocus?: boolean;
  // Keep plain Enter on touch devices too (a one-field dialog where Return means done).
  enterOnTouch?: boolean;
}

export const useFormKeyboard = ({ step, autoFocus = false, enterOnTouch = false }: FormKeyboardOptions = {}) => {
  const scopeRef = useRef<HTMLDivElement>(null);
  const busy = useRef(false);
  const pressedAt = useRef(0);
  const pressedByTap = useRef(false);
  const stepChangedAt = useRef(0);
  const firstStep = useRef(true);
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!autoFocus || !scopeRef.current) return;
    const field = firstField(scopeRef.current);
    if (field && !isCombobox(field)) field.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepChangedAt.current = Date.now();
    const scope = scopeRef.current;
    if (!scope || Date.now() - pressedAt.current > STEP_FOCUS_WINDOW_MS) return;
    pressedAt.current = 0;
    const intoField = !pressedByTap.current && hasFinePointer();
    setTimeout(() => {
      // With a mouse or keyboard, continue in the new step's first field; on touch only take focus off the
      // primary, so no on-screen keyboard opens.
      const field = intoField ? firstField(scope, true) : undefined;
      if (field) field.focus();
      else if ((document.activeElement as HTMLElement | null)?.hasAttribute("data-primary-action")) (document.activeElement as HTMLElement).blur();
    }, 0);
  }, [step]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" || e.defaultPrevented || e.repeat || e.altKey || e.shiftKey) return;
    const native = e.nativeEvent as KeyboardEvent;
    if (native.isComposing || native.keyCode === 229) return;
    const scope = scopeRef.current;
    const target = e.target as HTMLElement;
    if (!scope || !belongsTo(target, scope) || hasOpenModal(scope)) return;
    const chord = e.ctrlKey || e.metaKey;
    // Ctrl/Cmd+Enter from any field (textareas too), never from buttons or toggles, which act on their own.
    if (chord ? !isField(target) : !isSubmitField(target) || !(enterOnTouch || hasFinePointer())) return;
    const primary = primaryOf(scope);
    if (!primary) return;

    // preventDefault also stops a native <form> submit, which would reload the page.
    e.preventDefault();
    e.stopPropagation();
    if (busy.current) return;
    busy.current = true;
    // Blur first so onBlur handlers (price formatting, typed dropdown text) commit before saving.
    target.blur();
    setTimeout(() => {
      primary.click();
      setTimeout(() => {
        busy.current = false;
        // Back to the field after a failed save; not into a dropdown, whose list would reopen over the errors.
        if (target.isConnected && document.activeElement === document.body && !isCombobox(target)) target.focus();
      }, 0);
    }, 0);
  }, [enterOnTouch]);

  return {
    scopeRef,
    scopeProps: {
      ref: scopeRef,
      "data-form-scope": "",
      onKeyDown,
      onSubmit: (e: React.FormEvent) => e.preventDefault(),
      // Inline because the host build has no `contents` class; the wrapper adds no box.
      style: { display: "contents" } as React.CSSProperties,
    },
    // Ignores presses while the previous one's promise is pending, so a second Enter or click never saves twice.
    primaryProps: (onPress: (e: React.MouseEvent<HTMLElement>) => unknown) => ({
      "data-primary-action": "",
      "aria-busy": submitting || undefined,
      onClick: async (e: React.MouseEvent<HTMLElement>) => {
        if (inFlight.current || Date.now() - stepChangedAt.current < STEP_SETTLE_MS) return;
        pressedAt.current = Date.now();
        pressedByTap.current = isTap(e);
        inFlight.current = true;
        setSubmitting(true);
        try {
          await onPress(e);
        } finally {
          inFlight.current = false;
          setSubmitting(false);
        }
      },
    }),
    // For Back and any other button that changes the step: focus follows into the new step as after Next.
    stepProps: (onPress: (e: React.MouseEvent<HTMLElement>) => unknown) => ({
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        if (Date.now() - stepChangedAt.current < STEP_SETTLE_MS) return;
        pressedAt.current = Date.now();
        pressedByTap.current = isTap(e);
        onPress(e);
      },
    }),
    submitting,
  };
};

const focusablesOf = (root: HTMLElement) =>
  Array.from(
    root.querySelectorAll<HTMLElement>('input:not([type="hidden"]), select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])'),
  ).filter((el) => isVisible(el) && !(el as HTMLButtonElement).disabled);

// Moves focus into a dialog when it opens (its first field, else its first button; on touch only a
// button, so no keyboard pops up), keeps Tab inside it, closes it on an Escape no control inside
// used, and gives focus back to whatever had it before when it closes.
export const useDialogFocus = (open: boolean, dialogRef: React.RefObject<HTMLElement>, onClose?: () => void) => {
  const returnTo = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    const focusIn = () => {
      if (dialog.contains(document.activeElement)) return;
      const fields = hasFinePointer()
        ? Array.from(dialog.querySelectorAll<HTMLElement>('input:not([type="hidden"]), select, textarea')).filter((el) => isVisible(el) && !isCombobox(el))
        : [];
      (fields[0] ?? Array.from(dialog.querySelectorAll<HTMLElement>("button, [tabindex='0']")).find(isVisible))?.focus();
    };
    focusIn();
    // Again once a fade-in has run: an element mid visibility transition cannot take focus yet.
    const retry = setTimeout(focusIn, 320);
    // On the document, bubbling: React handlers inside (an open dropdown or date picker) run first.
    const onKey = (e: KeyboardEvent) => {
      if (!dialog.contains(e.target as Node) || e.defaultPrevented) return;
      if (e.key === "Escape" && closeRef.current) {
        e.preventDefault();
        closeRef.current();
      } else if (e.key === "Tab") {
        const items = focusablesOf(dialog);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(retry);
      document.removeEventListener("keydown", onKey);
      const back = returnTo.current;
      returnTo.current = null;
      // Only while focus is still in the dialog (or lost): never pull it from where the user clicked.
      const focusLost = () => dialog.contains(document.activeElement) || document.activeElement === document.body;
      const hadOpener = !!back && back !== document.body;
      let target: HTMLElement | undefined;
      if (hadOpener && back.isConnected) target = back;
      else if (hadOpener && hasFinePointer()) {
        // The opener is gone (its row was deleted): continue at the form's first field.
        const scope = dialog.parentElement?.closest<HTMLElement>("[data-form-scope]");
        target = scope ? firstField(scope, true) : undefined;
      }
      if (target) setTimeout(() => focusLost() && target?.focus({ preventScroll: true }), 0);
    };
  }, [open, dialogRef]);
};

export default useFormKeyboard;
